<template>
  <div>
    <b-container fluid class="px-5">

      <b-row class="mt-4">
        <b-col>
          <div class="d-lg-flex justify-content-lg-between">
            <div v-if="animal">
              <div class="d-flex align-items-start">
                <back-arrow back-to="/"/>
                <div>
                  <h1 class="page-title">{{animal.name}}</h1>
                  <h2 class="page-subtitle">{{animalAge != null ? (animalAge + " -") : ''}} {{animal.species.name}} ({{animal.species.family}})</h2>
                </div>
              </div>
            </div>
            <div class="mt-4 mt-lg-0">
              <b-button-group>
                <b-button variant="primary rounded ml-2 font-weight-bold" @click="clickOnCreateDailyStatusBtn" v-if="hospitalization.endDate == null">
                  <report-medical-icon size="18" class="mr-1" /> Nouvel état quotidien
                </b-button>
                <b-button variant="secondary rounded ml-2 font-weight-bold" @click="clickOnSendAlertBtn">
                  <template v-if="hospitalization.needAlert">
                    <bell-off-icon size="18" class="mr-1" /> Ne plus alerter le propriétaire
                  </template>
                  <template v-else>
                    <bell-icon size="18" class="mr-1" /> Alerter le propriétaire
                  </template>
                </b-button>
                <b-button
                  v-if="hospitalization && !hospitalization.endDate"
                  variant="success rounded ml-2 font-weight-bold"
                  @click="clickOnEndHospitalization">
                  <flag-icon size="18" class="mr-1" /> Clôturer l'hospitalisation
                </b-button>
                <b-button v-else variant="success rounded ml-2 font-weight-bold" @click="clickOnDownloadPDF">
                  <printer-icon size="18" class="mr-1"/> Télécharger PDF
                </b-button>
              </b-button-group>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col lg="4">
          <b-card
            v-if="hospitalization"
            title="Mémo interne"
            class="shadow-sm h-100"
          >
            <b-form-textarea
              id="internal-note-input"
              rows="5"
              placeholder="Ce cadre est destiné à une prise de note rapide. Le contenu ne sera pas visible par le propriétaire de l'animal."
              v-model="form.internalNote.value"
              @input="debouncedUpdateInternalNote"
            ></b-form-textarea>
          </b-card>
        </b-col>
        <b-col lg="8" class="mt-4 mt-lg-0">
          <b-card
            title="Infos générales"
            class="shadow-sm h-100"
          >
            <b-row v-if="animal && hospitalization">
              <b-col lg="6">
                <div class="mt-2">
                  <span class="font-weight-bold">Propriétaire</span> : <b-link :to="{name: 'UserShow', params: {userId: animal.user.id}}">{{animal.user.name}}</b-link>
                </div>
                <div>
                  <span class="font-weight-bold">Tél.</span> : {{animal.user.phone}}
                </div>
                <div class="mt-2">
                  <span class="font-weight-bold">Nom</span> : <b-link :to="{name: 'AnimalShow', params: {animalId: animal.id}}">{{animal.name}}</b-link>
                </div>
                <div class="mt-2" v-if="animal.dateOfBirth != null">
                  <span class="font-weight-bold">Age</span> : <b>{{animalAge}}</b> {{animal.dateOfBirthToString != null ? `(${animal.dateOfBirthToString})` : ''}}
                </div>
                <div class="mt-2" v-if="hospitalization">
                  <span class="font-weight-bold">Motif d'hospitalisation</span> : {{hospitalization.goal}}
                </div>
                <div class="mt-2" v-if="!hospitalization || hospitalization.endDate">
                  <b-form-checkbox
                    v-model="deadAnimal"
                    name="deadAnimal"
                    :value="true"
                    :unchecked-value="false"
                    @change="openSwitchIsDeadAnimalStatus()">
                    <strong>Animal décédé</strong>
                  </b-form-checkbox>
                </div>
              </b-col>
              <b-col lg="6">
                <div class="mt-2">
                  <span class="font-weight-bold">Temps d'hospitalisation</span> :
                  <template>{{TimeUtils.nbDaysRealised(hospitalization.startDate, hospitalization.endDate)}} jours réalisés</template>
                </div>
                <div class="mt-2">
                  <span class="font-weight-bold">Estimation de sortie</span> :
                  <template v-if="!hospitalization.endDate">{{outInNbDays}}</template>
                  <b-link @click="clickOnHistoriesLink">voir historique</b-link>
                </div>
                <div class="mt-2" v-if="hospitalization.endDate">
                  <span class="font-weight-bold">Date de sortie</span>: {{hospitalization.endDateToString}}
                </div>

                <div class="mt-2">
                  <b-form-checkbox
                    v-model="toReturnToday"
                    name="toReturnToday"
                    :value="true"
                    :unchecked-value="false"
                    @change="clickOnToReturn('day')"
                  >
                    <strong>A récupérer dans la journée</strong>
                  </b-form-checkbox>
                </div>
                <div>
                  <b-form-checkbox
                    v-model="toReturnTonight"
                    name="toReturnTonight"
                    :value="true"
                    :unchecked-value="false"
                    @change="clickOnToReturn('night')"
                  >
                    <strong>A récupérer le soir</strong>
                  </b-form-checkbox>
                </div>
              </b-col>
            </b-row>

          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col lg="6">
          <b-card class="shadow-sm h-100">
            <div class="d-flex justify-content-between">
              <div>
                <h4 class="card-title">Chirurgies</h4>
                <h6 class="card-subtitle text-muted mb-2">{{surgeries.length}} chirurgie{{surgeries.length > 1?'s':''}}</h6>
              </div>
              <div>
                <b-button
                  variant="outline-primary rounded ml-1"
                  :to="{ name: 'SurgeryForHospitalization', params: { hospitalizationId: hospitalizationId }}"
                >
                  Voir chirurgies
                </b-button>
              </div>
            </div>
            <div class="mt-2">
              <template v-for="(surgery, index) in surgeries">{{index > 0 ? ', ':''}}{{surgery.name}}</template>
            </div>
          </b-card>
        </b-col>
        <b-col lg="6" class="mt-4 mt-lg-0">
          <b-card class="shadow-sm h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h4 class="card-title">Notes</h4>
                <h6 class="card-subtitle text-muted mb-2">{{notes.length}} note{{notes.length > 1?'s':''}}</h6>
              </div>
              <b-button
                variant="outline-primary rounded ml-1"
                :to="{ name: 'NoteForHospitalization', params: { hospitalizationId: hospitalizationId }}"
              >
                Voir notes
              </b-button>
            </div>
            <div v-if="notes.length > 0" class="d-flex justify-content-between">
              <div class="font-weight-bold">{{notes[notes.length-1].title}}</div>
              <div>{{new Date(notes[notes.length-1].creationDate).toLocaleDateString('fr-FR')}}</div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-4" v-if="lastDailyStatus">
        <b-col>
          <daily-status-show-card
            title="Dernier état quotidien"
            :daily-status="lastDailyStatus"
          ></daily-status-show-card>
        </b-col>
      </b-row>

      <b-row class="mt-4 mb-4">
        <b-col>
          <daily-status-index
            v-if="hospitalization"
            :hospitalization="hospitalization"
            @didDeleteDailyStatus="fetchLastDailyStatusForHospitalization"
            @clickOnShowBtn="zoomOnDailyStatus"
          ></daily-status-index>
        </b-col>
      </b-row>

      <AnimalSurvey v-if="animal.surveySpecies != null" class="mb-4" :animalId="animal.id"/>

    </b-container>


    <!-- Hospitalization History Modal -->
    <hospitalization-history-modal v-if="hospitalization" :hospitalization="hospitalization"></hospitalization-history-modal>
    <!-- End Hospitalization modal -->
    <hospitalization-end-modal v-if="hospitalization" :hospitalization="hospitalization" @onHospitalizationEnded="onHospitalizationEnded()"></hospitalization-end-modal>
    <!-- Hospitalization Send Alert Modal -->
    <hospitalization-send-alert-modal v-if="hospitalization" :hospitalization="hospitalization" @didHandleAlert="didHandleAlert()"></hospitalization-send-alert-modal>
    <!-- Hospitalization Switch isDead Animal -->
    <hospitalization-confirm-animal-death-modal v-if="animal" :animal="animal" :is-dead="deadAnimal" @onAnimalDeathConfirmed="fetchAnimal"></hospitalization-confirm-animal-death-modal>

    <div v-if="ui.zoomOnDailyStatus" class="zoom-modal" @click="closeZoomModal">
      <span class="zoom-close">&times;</span>
      <div class="zoom-modal-content">
        <daily-status-show-card
          title="État quotidien"
          :daily-status="ui.zoomOnDailyStatus"
        ></daily-status-show-card>
      </div>
    </div>

  </div>

</template>

<script>

import _ from "lodash";
import api from '../../services/api';
import TimeUtils from "../../utils/TimeUtils";
import BackArrow from "../../components/BackArrow";
import ShowActions from "../../components/ShowActions";
import HospitalizationHistoryModal from "./HospitalizationHistoryModal";
import HospitalizationEndModal from "./HospitalizationEndModal";
import HospitalizationSendAlertModal from "./HospitalizationSendAlertModal";
import DailyStatusIndex from "../dailyStatus/DailyStatusIndex";
import DailyStatusShowCard from "../dailyStatus/DailyStatusShowCard";
import AnimalSurvey from "../animal/AnimalSurvey";
import HospitalizationConfirmAnimalDeathModal from "./HospitalizationConfirmAnimalDeathModal.vue";

export default {

  name: 'HospitalizationShow',

  components: {
    HospitalizationConfirmAnimalDeathModal,
    AnimalSurvey,
    BackArrow,
    DailyStatusIndex,
    DailyStatusShowCard,
    HospitalizationHistoryModal,
    HospitalizationEndModal,
    HospitalizationSendAlertModal,
    ShowActions
  },

  data() {
    return {
      TimeUtils: TimeUtils,
      hospitalizationId: this.$route.params.hospitalizationId,
      hospitalization: null,
      dateOfEnd: null,

      toReturnToday: false,
      toReturnTonight: false,

      animal: null,
      deadAnimal: false,
      animalAge:0,
      outInNbDays: 0,

      lastDailyStatus: null,

      surgeries: [],
      notes: [],

      debouncedUpdateInternalNote: null,

      ui: {
        zoomOnDailyStatus: null
      },

      form: {
        internalNote: {
          value: null
        }
      }
    }
  },

  created() {
    this.debouncedUpdateInternalNote = _.debounce(this.updateInternalNote, 1000)
    this.fetchData()
  },

  methods: {

    fetchData() {
      this.fetchHospitalization()
    },

    fetchHospitalization() {
      api
        .get(`/hospitalization/${this.hospitalizationId}`)
        .then(response => {
          this.hospitalization = response.data
          this.setInitialValues()
          this.fetchLastDailyStatusForHospitalization()
          this.fetchAnimal()
          this.fetchSurgeries()
          this.fetchNotes()
        })
    },

    fetchLastDailyStatusForHospitalization() {
      api
        .get(`/dailyStatus/getLastDailyStatusForHospitalization?hospitalizationId=${this.hospitalization.id}`)
        .then(response => {
          this.lastDailyStatus = response.data
        })
    },

    setInitialValues() {
      this.form.internalNote.value = this.hospitalization.internalNote
      this.toReturnToday = this.hospitalization.toReturnToday
      this.toReturnTonight = this.hospitalization.toReturnTonight
    },

    fetchAnimal(){
      api
        .get(`/animal/${this.hospitalization.animal.id}`)
        .then(response => {
          this.animal = response.data
          this.deadAnimal = this.animal.isDead
          this.animalAge = TimeUtils.setAnimalAge(this.animal.dateOfBirth)
          this.outInNbDays = TimeUtils.nbOfDaysBeforePresumedEndDate(this.hospitalization.presumedEndDate)
        })
    },

    fetchSurgeries(){
      api
        .get(`/surgery/getSurgeriesByHospitalization?hospitalizationId=${this.hospitalizationId}`)
        .then(response => {
          this.surgeries = response.data
        })
    },

    fetchNotes(){
      api
        .get(`/note/getNotesByHospitalization?hospitalizationId=${this.hospitalizationId}`)
        .then(response => {
          this.notes = response.data
        })
    },

    setTotalCount(totalCount) {
      this.totalCount = totalCount
    },

    clickOnToReturn(timeOfDay){
      if(timeOfDay === "day"){
        if(this.toReturnToday === true){
          this.toReturnTonight = false
        }
      }else{
        if(this.toReturnTonight === true){
          this.toReturnToday = false
        }
      }
      this.setToReturn(timeOfDay)
    },

    clickOnHistoriesLink() {
      this.$bvModal.show('hospitalization-history-modal')
    },

    clickOnCreateDailyStatusBtn() {
      this.$router.push({
        name: 'DailyStatusCreate',
        params: {
          hospitalizationId: this.hospitalizationId
        }
      })
    },

    clickOnEndHospitalization(){
      this.$bvModal.show('hospitalization-end-modal')
    },

    clickOnSendAlertBtn() {
      this.$bvModal.show('hospitalization-send-alert-modal')
    },

    didHandleAlert(){
      this.hospitalization.needAlert = !this.hospitalization.needAlert
    },

    clickOnDownloadPDF(thenRefresh){

      var data = new FormData();
      data.append('hospitalizationId', this.hospitalizationId);

      api({
        method: 'POST',
        url: '/hospitalization/generateReportPDF',
        data: data
      })
        .then(response => {
          //--------- DOWNLOAD -----------
          let a = document.createElement('a');
          if (window.URL && window.Blob && ('download' in a) && window.atob) {
            let blob = this.base64ToBlob(response.data.base64, 'application/pdf');
            let url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = response.data.fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            if(thenRefresh === true) {
              this.$router.go()
            }
          }
        });
      this.$bvModal.show('hospitalization-print-modal')
    },

    base64ToBlob: function (base64, mimetype, slicesize) {
      if (!window.atob || !window.Uint8Array) {
        // The current browser doesn't have the atob function. Cannot continue
        return null;
      }
      mimetype = mimetype || '';
      slicesize = slicesize || 512;
      let bytechars = atob(base64);
      let bytearrays = [];
      for (let offset = 0; offset < bytechars.length; offset += slicesize) {
        let slice = bytechars.slice(offset, offset + slicesize);
        let bytenums = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          bytenums[i] = slice.charCodeAt(i);
        }
        let bytearray = new Uint8Array(bytenums);
        bytearrays[bytearrays.length] = bytearray;
      }
      return new Blob(bytearrays, {type: mimetype});
    },

    onHospitalizationEnded(){
      this.clickOnDownloadPDF(true)
    },

    updateInternalNote() {

      let data = new FormData()
      data.append('hospitalizationId', this.hospitalizationId)
      data.append('internalNote', this.form.internalNote.value)

      api({
        method: 'POST',
        url: '/hospitalization/updateInternalNote',
        data: data
      })
        .then(response => {
        })
        .catch((error) => {
          if (error.response.status === 418) {
            this.$bvToast.toast(error.response.data.message, {
              autoHideDelay: 2000,
              toaster: 'b-toaster-bottom-right'
            })
          }
        });
    },

    setToReturn(timeOfDay){

      let data = new FormData()
      data.append('hospitalizationId', this.hospitalizationId)
      data.append('timeOfDay', timeOfDay)
      data.append('toReturn', timeOfDay === "day" ? this.toReturnToday : this.toReturnTonight)

      api({
        method: 'POST',
        url: '/hospitalization/setToReturn',
        data: data
      })
        .then(response => {
        })
        .catch((error) => {
          if (error.response.status === 418) {
            this.$bvToast.toast(error.response.data.message, {
              autoHideDelay: 2000,
              toaster: 'b-toaster-bottom-right'
            })
          }
        });
    },

    openSwitchIsDeadAnimalStatus() {
      this.$bvModal.show('hospitalization-confirm-animal-death-modal')
    },

    zoomOnDailyStatus(dailyStatus) {
      this.ui.zoomOnDailyStatus = dailyStatus
    },

    closeZoomModal() {
      this.ui.zoomOnDailyStatus = null
    }

  }
}
</script>

<style scoped>
.subtitle{
  color: var(--secondary-text-color);
  margin-top: -15px;
}
.btn-outline-primary{
  padding: 10px 10px;
}


/* The Modal (background) */
.zoom-modal {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgb(0,0,0);
  background-color: rgba(0,0,0,0.85);
  display: flex;
  justify-content: center;
  align-items: center;
}

.zoom-modal-content {
  min-width: 1000px;
  max-width: 80%;
  max-height: 100%;
}
@media only screen and (max-width: 700px){
  .zoom-modal-content {
    width: 100%;
  }
}

/* Add Animation */
.zoom-modal-content {
  -webkit-animation-name: zoom;
  -webkit-animation-duration: 0.2s;
  animation-name: zoom;
  animation-duration: 0.2s;
}

@-webkit-keyframes zoom {
  from {-webkit-transform:scale(0)}
  to {-webkit-transform:scale(1)}
}

@keyframes zoom {
  from {transform:scale(0)}
  to {transform:scale(1)}
}

/* The Close Button */
.zoom-close {
  position: absolute;
  top: 15px;
  right: 35px;
  color: var(--main-white);
  font-size: 40px;
  font-weight: bold;
  transition: 0.3s;
}

.zoom-close:hover,
.zoom-close:focus {
  color: var(--secondary-text-color);
  text-decoration: none;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/views/hospitalization/HospitalizationShow.vue